import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, Policy } from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useRolePermissions } from '../hooks/useRolePermissions';
import {
  TrashIcon,
  ShieldCheckIcon,
  DocumentIcon,
  FolderIcon,
  BuildingOfficeIcon,
  UserIcon,
  XMarkIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  EllipsisVerticalIcon,
  ArrowsUpDownIcon,
  FunnelIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

const Policies: React.FC = () => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sortField, setSortField] = useState<'policy_type' | 'asset_type' | 'status' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<Policy | null>(null);
  const [openPolicyMenu, setOpenPolicyMenu] = useState<string | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const permissions = useRolePermissions();
  

  // Set page title
  usePageTitle('Policies', 'Manage security policies and access controls');

  // Close menu when clicking outside
  useEffect(() => {
    if (!openPolicyMenu) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('[data-policy-menu]')) {
        setOpenPolicyMenu(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPolicyMenu]);



  const canDeletePolicy = (policyType: string) => {
    const canDelete = (() => {
      switch (policyType) {
        case 'system':
          return permissions.canDeleteSystemPolicies;
        case 'company':
          return permissions.canDeleteCompanyPolicies;
        case 'user':
          return permissions.canDeleteUserPolicies;
        default:
          return false;
      }
    })();
    
    return canDelete;
  };

  const canTemplatePolicy = (policyType: string) => {
    switch (policyType) {
      case 'system':
        return permissions.canTemplateSystemPolicies;
      case 'company':
        return permissions.canTemplateCompanyPolicies;
      case 'user':
        return permissions.canTemplateUserPolicies;
      default:
        return false;
    }
  };

  // Policy type filters only for regular users (Company + User, no System)
  const getAvailablePolicyTypes = () => {
    if (!user || user.role !== 'user') return [];
    const types: { value: string; label: string }[] = [];
    if (user.company_id) types.push({ value: 'company', label: 'Company' });
    types.push({ value: 'user', label: 'User' });
    return types;
  };

  // Fetch policies - backend handles role-based filtering automatically.
  // Include company_id in key so cache is busted when user's company changes.
  const { data: policies, isLoading, error } = useQuery({
    queryKey: ['policies', user?.company_id ?? null],
    queryFn: () => apiService.getPoliciesByRole(),
  });

  // Filter policies by type (user role only), asset type, and status (frontend filtering for UI)
  const filteredPolicies = policies?.filter(policy => {
    const typeMatch = selectedTypes.length === 0 || selectedTypes.includes(policy.type);
    const assetTypeMatch = selectedAssetTypes.length === 0 || selectedAssetTypes.includes(policy.asset_type);
    const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(policy.status);
    return typeMatch && assetTypeMatch && statusMatch;
  }).sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'policy_type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'asset_type':
        comparison = a.asset_type.localeCompare(b.asset_type);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'date':
      default:
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
        comparison = dateB - dateA;
        break;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  }) || [];

  // Delete policy mutation
  const deletePolicyMutation = useMutation({
    mutationFn: (id: string) => apiService.deletePolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policies'] });
      setShowDeleteConfirm(false);
      setPolicyToDelete(null);
    },
  });

  const handleDeletePolicy = (policy: Policy) => {
    setPolicyToDelete(policy);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePolicy = () => {
    if (policyToDelete) {
      deletePolicyMutation.mutate(policyToDelete.id);
    }
  };

  // View-only: company admin + system policy; regular user + company policy. Else open edit.
  const handleCardClick = (policy: Policy) => {
    const viewOnly =
      (user?.role === 'admin' && policy.type === 'system') ||
      (user?.role === 'user' && policy.type === 'company');
    if (viewOnly) {
      navigate(`/policies/${policy.id}`);
    } else {
      navigate(`/policies/${policy.id}/edit`);
    }
  };

  const handleTemplatePolicy = (policy: Policy) => {
    navigate(`/policies/create?template=${policy.id}`);
  };

  // Chip filtering functions
  const toggleTypeFilter = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const toggleAssetTypeFilter = (assetType: string) => {
    setSelectedAssetTypes(prev => 
      prev.includes(assetType) 
        ? prev.filter(t => t !== assetType)
        : [...prev, assetType]
    );
  };

  const toggleStatusFilter = (status: string) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const handleSortFieldChange = (field: 'policy_type' | 'asset_type' | 'status' | 'date') => {
    setSortField(field);
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const clearAllFilters = () => {
    setSelectedTypes([]);
    setSelectedAssetTypes([]);
    setSelectedStatuses([]);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return selectedTypes.length > 0 || selectedAssetTypes.length > 0 || selectedStatuses.length > 0;
  };

  const truncateDescription = (text: string, maxLength: number = 40) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getPolicyTypeIcon = (type: string) => {
    switch (type) {
      case 'system':
        return <ShieldCheckIcon className="w-5 h-5 text-blue-500" />;
      case 'company':
        return <BuildingOfficeIcon className="w-5 h-5 text-green-500" />;
      case 'user':
        return <UserIcon className="w-5 h-5 text-gray-500" />;
      default:
        return <ShieldCheckIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getAssetTypeIcon = (assetType: string) => {
    return assetType === 'file' ? (
      <DocumentIcon className="w-4 h-4 text-blue-500" />
    ) : (
      <FolderIcon className="w-4 h-4 text-green-500" />
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Returns yyyy-mm-dd HH:MM:SS format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };


  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">Error loading policies</div>
        <p className="text-gray-600">Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls Row - Sort and Filter */}
      <div className="flex items-center justify-between gap-2 sm:gap-3 mb-4">
        {/* Left: Sort Controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="hidden sm:inline text-sm text-gray-500">Sort by:</span>
          <select
            value={sortField}
            onChange={(e) => handleSortFieldChange(e.target.value as 'policy_type' | 'asset_type' | 'status' | 'date')}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 pr-8 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px]"
          >
            <option value="date">Date</option>
            <option value="policy_type">Policy Type</option>
            <option value="asset_type">Asset Type</option>
            <option value="status">Status</option>
          </select>
          <button
            onClick={toggleSortOrder}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            <ArrowsUpDownIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Right: Filter Icon */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className={`p-2 rounded-md transition-colors ${
            filtersExpanded || hasActiveFilters()
              ? 'bg-blue-100 text-blue-700 border border-blue-300'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 border border-gray-300'
          }`}
          title="Toggle filters"
        >
          <FunnelIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Filters - Accordion */}
      {filtersExpanded && (
        <div className="card mb-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Filters</h3>
            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
              >
                <XMarkIcon className="w-4 h-4 mr-1" />
                <span>Reset Filters</span>
              </button>
            )}
        </div>
        
          {/* Filters Content */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
            {/* Policy Type Chips - only for user role (Company + User, no System) */}
            {getAvailablePolicyTypes().length > 0 && (
              <>
                <div className="flex flex-wrap gap-2">
                  {getAvailablePolicyTypes().map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => toggleTypeFilter(filter.value)}
                      className={`inline-flex items-center px-3 py-1.5 text-sm font-medium transition-colors border-2 ${
                        selectedTypes.includes(filter.value)
                          ? 'bg-governate-50 text-governate-700 border-governate-500'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {getPolicyTypeIcon(filter.value)}
                      <span className="ml-2">{filter.label}</span>
                    </button>
                  ))}
                </div>
                <div className="md:hidden border-t border-gray-200" />
              </>
            )}
            {/* Asset Type Chips */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'file', label: 'Files', icon: DocumentIcon },
                { value: 'folder', label: 'Folders', icon: FolderIcon },
              ].map((assetType) => (
                <button
                  key={assetType.value}
                  onClick={() => toggleAssetTypeFilter(assetType.value)}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium transition-colors border-2 ${
                    selectedAssetTypes.includes(assetType.value)
                      ? 'bg-governate-50 text-governate-700 border-governate-500'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <assetType.icon className="w-4 h-4" />
                  <span className="ml-2">{assetType.label}</span>
                </button>
              ))}
            </div>

            {/* Divider - Mobile only */}
            <div className="md:hidden border-t border-gray-200"></div>

            {/* Status Chips */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'active', label: 'Active', icon: CheckCircleIcon },
                { value: 'draft', label: 'Draft', icon: DocumentTextIcon },
                { value: 'inactive', label: 'Inactive', icon: ClockIcon },
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => toggleStatusFilter(status.value)}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium transition-colors border-2 ${
                    selectedStatuses.includes(status.value)
                      ? 'bg-governate-50 text-governate-700 border-governate-500'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <status.icon className="w-4 h-4" />
                  <span className="ml-2">{status.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Policies List - Cards */}
      <div className="card">
        {isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : filteredPolicies.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No policies found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {!hasActiveFilters()
                ? 'Get started by creating a new policy.'
                : 'No policies match the selected filters.'
              }
            </p>
            <div className="mt-6">
              <Link
                to="/policies/new"
                className="btn-create-policy inline-flex items-center justify-center space-x-2"
              >
                <PlusIcon className="w-5 h-5" />
                <span>New Policy</span>
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-0">
            {/* Policy Count Header */}
            <div className="px-4 py-2 border-b border-gray-200">
              <span className="text-sm text-gray-500">
                {filteredPolicies.length} polic{filteredPolicies.length !== 1 ? 'ies' : 'y'}
              </span>
            </div>

            {/* Policy Cards */}
            {filteredPolicies.map((policy) => (
              <div
                key={policy.id}
                onClick={() => handleCardClick(policy)}
                className="border-l-4 border-l-transparent bg-white hover:bg-gray-50 transition-all cursor-pointer px-2 sm:px-3 py-2 sm:py-2.5 border-b border-gray-200"
              >
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  {/* Left: Policy Info */}
                  <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                    {/* Desktop: Policy Type Icon & Status Circle */}
                    <div className="hidden sm:flex flex-col items-center gap-1 flex-shrink-0">
                      {/* Policy Type Icon - First */}
                    <div className="flex-shrink-0">
                      {getPolicyTypeIcon(policy.type)}
                      </div>
                      {/* Status Circle - Below */}
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                        policy.status === 'active' ? 'bg-green-500' : 
                        policy.status === 'draft' ? 'bg-yellow-500' : 
                        'bg-red-500'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {policy.name}
                        </div>
                      </div>
                      {/* Description: Full on desktop, truncated on mobile */}
                      <div className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-0.5">
                        <span className="sm:inline hidden">{policy.description || ''}</span>
                        <span className="sm:hidden">{truncateDescription(policy.description || '', 40)}</span>
                      </div>
                      {/* Mobile: Icons row below description */}
                      <div className="mt-2 sm:hidden flex items-center gap-2">
                        {/* Policy Type Icon */}
                        <div className="flex-shrink-0">
                          {getPolicyTypeIcon(policy.type)}
                        </div>
                        {/* Asset Type Icon */}
                        <div className="flex-shrink-0">
                          {getAssetTypeIcon(policy.asset_type)}
                        </div>
                        {/* Status Circle */}
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                          policy.status === 'active' ? 'bg-green-500' : 
                          policy.status === 'draft' ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`} />
                        {/* Status Text */}
                        <span className="text-xs text-gray-500 capitalize">
                          {policy.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Desktop Info & Actions */}
                  <div className="hidden md:flex items-center gap-4 flex-shrink-0">
                    {/* Desktop: Asset Type & Date Column */}
                    <div className="flex flex-col items-start gap-0.5 text-sm text-gray-500">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-shrink-0 flex items-center">
                          {getAssetTypeIcon(policy.asset_type)}
                        </div>
                        <span className="capitalize whitespace-nowrap">{policy.asset_type}</span>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {formatDate(policy.created_at).split(' ')[0]}
                      </span>
                    </div>

                    {/* Desktop: Action Icons - Template and Delete only (no View; card click opens view or edit by role/policy type) */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canTemplatePolicy(policy.type) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTemplatePolicy(policy);
                          }}
                          className="w-7 h-7 flex items-center justify-center text-blue-600 hover:text-blue-900 transition-colors"
                          title="Create from Template"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="w-7 h-7 flex-shrink-0"></div>
                      )}
                      {canDeletePolicy(policy.type) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePolicy(policy);
                          }}
                          className="w-7 h-7 flex items-center justify-center text-red-600 hover:text-red-900 transition-colors"
                          title="Delete Policy"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="w-7 h-7 flex-shrink-0"></div>
                      )}
                    </div>
                    </div>

                    {/* Mobile: Actions Menu */}
                    <div className="md:hidden flex items-center gap-2 flex-shrink-0">
                      {/* Actions Menu */}
                      <div className="relative" data-policy-menu>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenPolicyMenu(openPolicyMenu === policy.id ? null : policy.id);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="More options"
                        >
                          <EllipsisVerticalIcon className="w-5 h-5" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        {openPolicyMenu === policy.id && (
                          <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10" onClick={(e) => e.stopPropagation()}>
                            <div className="py-1">
                              {/* Template */}
                              {canTemplatePolicy(policy.type) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTemplatePolicy(policy);
                                    setOpenPolicyMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <DocumentDuplicateIcon className="w-4 h-4" />
                                  Create from Template
                                </button>
                              )}
                              
                              {/* Created at */}
                              <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
                                <div className="text-xs text-gray-500 dark:text-gray-400">Created at:</div>
                                <div className="text-sm text-gray-900 dark:text-gray-100">{formatDate(policy.created_at)}</div>
                              </div>
                              
                              {/* Delete */}
                              {canDeletePolicy(policy.type) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePolicy(policy);
                                    setOpenPolicyMenu(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                  Delete Policy
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && policyToDelete && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => {
            setShowDeleteConfirm(false);
            setPolicyToDelete(null);
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 border-2 border-red-600 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Policy</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 mb-2">
                Are you sure you want to permanently delete:
              </p>
              <p className="text-sm font-semibold text-gray-900 bg-gray-50 p-2 rounded">
                {policyToDelete.name}
              </p>
              <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">⚠️ Warning:</p>
                <ul className="text-sm text-red-700 mt-1 ml-4 list-disc">
                  <li>Policy will be permanently removed</li>
                  <li>This action cannot be reversed</li>
                </ul>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPolicyToDelete(null);
                }}
                disabled={deletePolicyMutation.isPending}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePolicy}
                disabled={deletePolicyMutation.isPending}
                className="flex-1 px-4 py-2 bg-white text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center space-x-2"
              >
                {deletePolicyMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


export default Policies;
